import { useEffect, useState } from "react";
import { api, BRL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, PieChart, Pie, Cell } from "recharts";
import { ArrowUpRight, Clock, CheckCircle2, AlertTriangle, Landmark, PieChart as PieIcon } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

function StatCard({ label, value, icon: Icon, tone = "default", testid, sub }) {
  const tones = {
    default: "text-foreground",
    warn: "text-amber-600 dark:text-amber-500",
    danger: "text-destructive",
    good: "text-primary",
  };
  return (
    <Card className="p-6 border-border/60 shadow-none rounded-md" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-muted-foreground font-medium">{label}</span>
        <Icon className={`h-4 w-4 ${tones[tone]}`} aria-hidden />
      </div>
      <p className={`font-head text-3xl font-extrabold tracking-tight tnum ${tones[tone]}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [forecast, setForecast] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setStats(r.data));
    api.get("/forecast?months=6").then((r) => setForecast(r.data.months));
  }, []);

  return (
    <div className="fade-up space-y-8">
      <div>
        <h1 className="font-head text-4xl font-extrabold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Visão geral das suas finanças</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {!stats ? (
          [...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-md" />)
        ) : (
          <>
            <StatCard label="Saldo em Caixa" value={BRL(stats.balance)} icon={Landmark} tone={stats.balance >= 0 ? "good" : "danger"} testid="stat-balance" sub={`Entradas: ${BRL(stats.total_income)}`} />
            <StatCard label="Total a Pagar" value={BRL(stats.total_pending)} icon={Clock} tone="warn" testid="stat-pending" sub={`${stats.count} despesas`} />
            <StatCard label="Total Pago" value={BRL(stats.total_paid)} icon={CheckCircle2} tone="good" testid="stat-paid" />
            <StatCard label="Vencidas" value={BRL(stats.overdue)} icon={AlertTriangle} tone="danger" testid="stat-overdue" sub={`${stats.overdue_count} em atraso`} />
          </>
        )}
      </div>

      <Card className="p-6 border-border/60 shadow-none rounded-md" data-testid="cashflow-chart">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-head text-lg font-bold tracking-tight">Fluxo de caixa projetado</h2>
            <p className="text-sm text-muted-foreground">Despesas provisionadas por mês</p>
          </div>
          <ArrowUpRight className="h-5 w-5 text-primary" aria-hidden />
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={forecast} margin={{ left: 0, right: 8, top: 8 }}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(221 83% 53%)" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="hsl(221 83% 53%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(214 20% 88%)" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => BRL(v)} contentStyle={{ borderRadius: 8, border: "1px solid hsl(214 20% 88%)", fontSize: 13 }} />
              <Area type="monotone" dataKey="total" stroke="hsl(221 83% 53%)" strokeWidth={2.5} fill="url(#g)" name="Despesas" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6 border-border/60 shadow-none rounded-md" data-testid="category-chart">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-head text-lg font-bold tracking-tight">Gastos por categoria</h2>
            <p className="text-sm text-muted-foreground">Total de despesas agrupadas por categoria</p>
          </div>
          <PieIcon className="h-5 w-5 text-primary" aria-hidden />
        </div>
        {!stats ? (
          <Skeleton className="h-72 rounded-md" />
        ) : (stats.categories || []).length === 0 ? (
          <div className="h-40 flex items-center justify-center text-muted-foreground text-sm" data-testid="category-empty">
            Nenhuma despesa com categoria ainda.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={stats.categories} dataKey="total" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2}>
                    {stats.categories.map((c, i) => <Cell key={i} fill={c.color} />)}
                  </Pie>
                  <Tooltip formatter={(v) => BRL(v)} contentStyle={{ borderRadius: 8, border: "1px solid hsl(214 20% 88%)", fontSize: 13 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-3">
              {stats.categories.map((c, i) => (
                <div key={i} className="flex items-center justify-between gap-3" data-testid={`category-legend-${i}`}>
                  <span className="flex items-center gap-2 text-sm">
                    <span className="h-3 w-3 rounded-full" style={{ background: c.color }} />
                    {c.name}
                  </span>
                  <span className="font-semibold tnum text-sm">{BRL(c.total)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
